// Shared trailing-pipeline decode.
//
// A source op (`query` / `datapoints`) takes its constraint pipeline as
// trailing op-call-shaped forms — `(filter #(…))`, `(columns […])`,
// `(limit n)`, `(sort …)` — read STRUCTURALLY, not dispatched as ops.
// `decodePipeline` decodes those forms ONCE into `DecodedParams`:
//
//  - `rawFilter` — the `:filter` lambda body verbatim (full fidelity:
//    relation-subpaths, unary leaves, direct-relation typing survive).
//    The partition planner and CDF's `translate` consume THIS.
//  - `filter` — a best-effort `DecodedPredicate` tree so a facade's
//    `rewrite` can branch on `(col, op)`; a leaf this simple shape
//    cannot represent still lives in `rawFilter`.
//  - `columns` / `limit` / `sort` — the non-predicate constraints.
//
// `rowColLabels` (the row-scoped column-ref decoder) lives HERE as the
// single source of truth; the CDF push-down recognizers in
// `pushdown.ts` import it rather than keeping a duplicate copy.

import { colNodeLabels } from '../column-ref.ts';
import { type AstInput, astShape, embeddedNumber, extractLambdaBody, type OpCall } from '../normalize.ts';
import { columnNameAstList } from '../ops/transform/helpers.ts';
import type { DecodedLeaf, DecodedParams, DecodedPredicate } from './types.ts';

/**
 * Decode a row-scoped column reference into its label path — the names
 * AFTER the row head. Accepts both shapes a `:filter` body can carry
 * for a column of the queried row:
 *
 *   - a bare column symbol `{$sym: 'type'}` → `['type']` (a user-written
 *     bare column ref, e.g. `#(= type "x")`);
 *   - the canonical `(col % <label>…)` form → the labels after `%`, e.g.
 *     `(col % type)` → `['type']`, `(col % rel externalId)` →
 *     `['rel', 'externalId']`.
 *
 * Returns `undefined` for a foreign `(col <ds> …)` ref (head ≠ `%`), an
 * expression-headed `(col (expr) …)`, or any non-column node — none of
 * which name a column of the queried row. SSOT for "which column of the
 * row does this leaf LHS name", shared by the CDF push-down recognizers
 * (`pushdown.ts`) and the facade-convenience leaf decode below.
 */
export const rowColLabels = (node: AstInput): readonly string[] | undefined => {
  const s = astShape(node);
  if (s.kind === 'symbolRef') {
    return [s.sym];
  }
  const labels = colNodeLabels(node);
  return labels !== undefined && labels[0] === '%' ? labels.slice(1) : undefined;
};

/** Normalize an `OpCall`'s `params` to a definite array. */
const callParams = (call: OpCall): readonly AstInput[] => (call.params ?? []) as readonly AstInput[];

/**
 * The boolean-combinator op names a predicate tree is built from. SSOT for
 * "which heads are boolean structure (recurse) vs comparison leaves (decode
 * / collapse)", shared by the best-effort decoder here and the
 * nested-sub-query pre-pass (`nested.ts`), which walk the same predicate
 * skeleton.
 */
export const BOOLEAN_COMBINATOR_NAMES: ReadonlySet<string> = new Set(['and', 'or', 'not']);
/**
 * Predicate leaf op names the best-effort decoder recognizes — the
 * engine's existing registered scalar predicates. The decoder introduces
 * NO new operators; it reads `DecodedLeaf.op` directly from a leaf call's
 * head symbol. Membership only gates whether a leaf is surfaced in the
 * convenience `filter` tree — the full-fidelity `rawFilter` always
 * carries every leaf regardless.
 */
const LEAF_PREDICATE_NAMES: ReadonlySet<string> = new Set([
  '=',
  '!=',
  'in',
  '<',
  '<=',
  '>',
  '>=',
  'prefix',
  're-match',
  'is-null',
  'is-not-null',
  'fuzzy',
]);
const UNARY_LEAF_NAMES: ReadonlySet<string> = new Set(['is-null', 'is-not-null']);

/**
 * Best-effort decode of a predicate-body AST into a `DecodedPredicate`
 * tree. Recurses through `and` / `or` / `not`; a leaf whose head is a
 * registered scalar predicate and whose LHS is a simple row column
 * (`rowColLabels` length 1) becomes a `DecodedLeaf`. Anything
 * else — a complex LHS (relation subpath), an unrecognised head, a
 * boolean wrapping a non-decodable child — yields `undefined`, signalling
 * "use `rawFilter` for this subtree". The facade layer treats the tree
 * as a convenience only; correctness lives in `rawFilter`.
 */
const decodePredicate = (node: AstInput): DecodedPredicate | undefined => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return undefined;
  }
  const { name } = shape.call;
  const params = callParams(shape.call);

  if (BOOLEAN_COMBINATOR_NAMES.has(name)) {
    const children = params.map((p) => decodePredicate(p as AstInput));
    if (children.some((c) => c === undefined)) {
      return undefined;
    }
    const decoded = children as readonly DecodedPredicate[];
    if (name === 'not') {
      return decoded.length === 1 ? { not: decoded[0] as DecodedPredicate } : undefined;
    }
    return name === 'and' ? { and: decoded } : { or: decoded };
  }

  if (!LEAF_PREDICATE_NAMES.has(name)) {
    return undefined;
  }
  const labels = rowColLabels(params[0] as AstInput);
  if (labels?.length !== 1) {
    return undefined;
  }
  const leaf: DecodedLeaf = UNARY_LEAF_NAMES.has(name)
    ? { col: labels[0] as string, op: name }
    : { col: labels[0] as string, op: name, value: params[1] as AstInput };
  return leaf;
};

/**
 * Decode a single trailing pipeline form into a partial `DecodedParams`.
 * Returns an empty object for an unrecognised form so the fold leaves the
 * accumulator untouched.
 */
const decodeForm = (form: AstInput): DecodedParams => {
  const shape = astShape(form);
  if (shape.kind !== 'opCall') {
    return {};
  }
  const { name } = shape.call;
  const params = callParams(shape.call);
  if (name === 'filter') {
    const extracted = extractLambdaBody(params[0]);
    if (extracted === undefined) {
      return {};
    }
    const decodedFilter = decodePredicate(extracted[0]);
    return { rawFilter: extracted[0], ...(decodedFilter === undefined ? {} : { filter: decodedFilter }) };
  }
  if (name === 'columns') {
    return { columns: columnNameAstList(params[0]) };
  }
  if (name === 'limit') {
    const lim = embeddedNumber(params[0] as AstInput);
    return lim === undefined ? {} : { limit: lim };
  }
  if (name === 'sort') {
    return { sort: form };
  }
  return {};
};

/**
 * Decode the trailing pipeline forms of a source op into
 * `DecodedParams`. Each `form` is an op-call-shaped AST node read
 * structurally:
 *
 *  - `(filter <lambda>)` → `rawFilter` (lambda body verbatim) +
 *    best-effort `filter` tree.
 *  - `(columns [:a :b])` → `columns`.
 *  - `(limit n)` → `limit`.
 *  - `(sort …)` → `sort` (the ORIGINAL form, verbatim, so a non-absorbing
 *    provider can re-emit it losslessly as a residual op).
 *
 * Unrecognised forms are ignored here. `DecodedParams.rest`
 * (non-constraint trailing ops as residual `PlanNode`s) is left unset:
 * a `PlanNode` carries a resolved `schemaAfter`, which this
 * schema-unaware decode cannot compute — the legality planner that owns
 * a schema populates `rest`.
 *
 * A later form of the same kind wins (last-write), matching the
 * left-to-right read order; duplicate constraints are a caller concern.
 */
export const decodePipeline = (forms: readonly AstInput[]): DecodedParams =>
  // Merge each recognized constraint form into one params object. A later
  // form of the same kind overwrites an earlier one (last-write, matching
  // left-to-right read order) — `Object.assign` applies in argument order,
  // so the rightmost form wins. A single mutable target avoids the O(n²)
  // accumulator spread.
  Object.assign({}, ...forms.map(decodeForm)) as DecodedParams;
/**
 * Find the first leaf in a best-effort decoded predicate tree that
 * names `col`. A facade's `rewrite` branches on `(col, op)`
 * ergonomically instead of walking raw AST — this is the shared walker
 * that descends `and` / `or` / `not` combinators and returns the
 * matching leaf (or `undefined` when no leaf names the column).
 *
 * The match is by leaf column name only; the caller reads `.op` /
 * `.value` off the returned leaf to discriminate (e.g. a `fuzzy`
 * predicate vs an `=`). Because the tree is best-effort, a leaf the
 * decoder could not represent simply does not appear here — the facade
 * then sees no leaf for that column and takes its default branch.
 */
export const findFacadeLeaf = (predicate: DecodedPredicate | undefined, col: string): DecodedLeaf | undefined => {
  if (predicate === undefined) {
    return undefined;
  }
  if ('and' in predicate || 'or' in predicate) {
    const children = 'and' in predicate ? predicate.and : predicate.or;
    return children.map((child) => findFacadeLeaf(child, col)).find((leaf) => leaf !== undefined);
  }
  if ('not' in predicate) {
    return findFacadeLeaf(predicate.not, col);
  }
  return predicate.col === col ? predicate : undefined;
};
